import React from 'react'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Flex } from '../../atoms/Flex'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Badge } from '../../atoms/Badge'
import { styled } from 'styled-components'
import type { Transaction } from '@popety-io/types'
import { getSavedTransactionLocation } from './SavedTransactionsGrid.utils'

export interface SavedTransactionsGridProps {
  transactions: Transaction[]
  onTransactionClick: (transaction: Transaction) => void
  onRemoveTransaction?: (transactionId: string) => void
}

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px;
  padding: 10px;
`

const TransactionCard = styled(Box)`
  background: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  border: 1px solid #e5e7eb;
  padding: 12px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
  }
`

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border-radius: 50%;
  width: 23px;
  height: 23px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;

  ${TransactionCard}:hover & {
    opacity: 1;
  }

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`

const TransactionBadge = styled(Badge)<{
  $backgroundColor?: string
  $textColor?: string
}>`
  margin-bottom: 6px;
  ${({ $backgroundColor }) =>
    $backgroundColor && `background-color: ${$backgroundColor};`}
  ${({ $textColor }) => $textColor && `color: ${$textColor};`}
  border: none;
`

const TransactionId = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  font-family: monospace;
  margin-bottom: 3px;
`

const PriceRow = styled(Flex)`
  align-items: baseline;
  gap: 3px;
  margin-bottom: 6px;
`

const MainPrice = styled(Text)`
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.3px;
`

const PricePerM2 = styled(Text)`
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
`

const LocationRow = styled(Flex)`
  align-items: center;
  gap: 3px;
  margin-bottom: 6px;
`

const LocationText = styled(Text)`
  font-size: 10px;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DetailRow = styled(Flex)`
  align-items: center;
  gap: 10px;
  padding-top: 6px;
  border-top: 1px solid #f3f4f6;
  margin-top: 6px;
`

const DetailItem = styled(Flex)`
  align-items: center;
  gap: 3px;
`

const DetailLabel = styled(Text)`
  font-size: 10px;
  color: #9ca3af;
`

const DetailValue = styled(Text)`
  font-size: 10px;
  font-weight: 600;
  color: #374151;
`

const getTransactionTypeColor = (type?: string): string => {
  const colorMap: Record<string, string> = {
    achat: '#00ADE6',
    vente: '#00B341',
    donation: '#FF9500',
    succession: '#8E8E93',
    heritage: '#8E8E93',
    echange: '#805AD5',
    partage: '#FF3B30',
    jugement: '#FF6B6B',
  }
  return colorMap[type?.toLowerCase() || ''] || '#8E8E93'
}

export const SavedTransactionsGrid: React.FC<SavedTransactionsGridProps> = ({
  transactions,
  onTransactionClick,
  onRemoveTransaction,
}) => {
  const formatCurrency = (value?: number) => {
    if (!value) return '—'
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      const date = new Date(dateString)
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '—'
      }
      return date.toLocaleDateString('de-CH')
    } catch {
      return '—'
    }
  }

  const getTotalArea = (transaction: Transaction) => {
    if (!transaction.transaction_properties) return 0
    return transaction.transaction_properties.reduce(
      (sum, p) => sum + (p.surface_area_total || 0),
      0
    )
  }

  const getPricePerM2 = (transaction: Transaction) => {
    const price = transaction.transaction_total_price
    const area = getTotalArea(transaction)
    if (!price || !area || area === 0) return undefined
    return Math.round(price / area)
  }

  return (
    <GridContainer>
      {transactions.map((transaction) => {
        const pricePerM2 = getPricePerM2(transaction)

        return (
          <TransactionCard
            key={transaction.id}
            onClick={() => onTransactionClick(transaction)}
          >
            {onRemoveTransaction && (
              <RemoveButton
                size="small"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemoveTransaction(transaction.id)
                }}
                title="Remove from saved"
                aria-label="Remove from saved"
              >
                <Icon name="x" size="small" />
              </RemoveButton>
            )}

            <TransactionBadge
              variant="secondary"
              size="small"
              $backgroundColor={`${getTransactionTypeColor(transaction.transaction_step_name)}20`}
              $textColor={getTransactionTypeColor(
                transaction.transaction_step_name
              )}
            >
              {transaction.transaction_step_name || 'Unknown'}
            </TransactionBadge>

            <TransactionId>
              {transaction.official_id ||
                transaction.transaction_id ||
                transaction.id}
            </TransactionId>

            <PriceRow>
              <MainPrice>
                {formatCurrency(transaction.transaction_total_price)}
              </MainPrice>
              {pricePerM2 && (
                <PricePerM2>{formatCurrency(pricePerM2)}/m²</PricePerM2>
              )}
            </PriceRow>

            <LocationRow>
              <Icon name="map-pin" size="small" color="#9ca3af" />
              <LocationText>
                {getSavedTransactionLocation(transaction)}
              </LocationText>
            </LocationRow>

            <DetailRow>
              <DetailItem>
                <Icon name="calendar" size="small" color="#9ca3af" />
                <DetailValue>
                  {formatDate(transaction.transaction_date)}
                </DetailValue>
              </DetailItem>

              {transaction.buyers_count > 0 && (
                <DetailItem>
                  <DetailLabel>Buyers:</DetailLabel>
                  <DetailValue>{transaction.buyers_count}</DetailValue>
                </DetailItem>
              )}

              {transaction.sellers_count > 0 && (
                <DetailItem>
                  <DetailLabel>Sellers:</DetailLabel>
                  <DetailValue>{transaction.sellers_count}</DetailValue>
                </DetailItem>
              )}

              {transaction.properties_count > 0 && (
                <DetailItem>
                  <DetailLabel>Props:</DetailLabel>
                  <DetailValue>{transaction.properties_count}</DetailValue>
                </DetailItem>
              )}
            </DetailRow>
          </TransactionCard>
        )
      })}
    </GridContainer>
  )
}
